/**
 * src/lib/api/notes.js — Collaborative Notes client API.
 *
 * Talks to /api/data?action=notes.* (GET-style list, POST create/archive/update).
 * Returns raw snake_case Supabase rows (see SHARED CONTRACT / SHAPE_CONTRACT.md).
 *
 * Scopes: 'project' (project_id), 'bundle' (bundle_id), 'general' (org-wide,
 * both ids null). source defaults to 'chat' from the UI; the voice path passes
 * source='voice'.
 */

import { apiFetch, API_BASE } from './client';

const DATA = `${API_BASE}/data`;

/**
 * List notes for a scope. Returns an array of raw note rows (newest first,
 * archived rows excluded server-side). Returns [] on a non-array payload.
 * @param {{scope:'project'|'bundle'|'general', projectId?:string, bundleId?:string}} args
 */
export async function listNotes({ scope, projectId, bundleId, taskId } = {}) {
  const params = new URLSearchParams({ action: 'notes.list', scope });
  if (projectId) params.set('project_id', projectId);
  if (bundleId) params.set('bundle_id', bundleId);
  if (taskId) params.set('task_id', taskId); // TICKET-735
  const data = await apiFetch(`${DATA}?${params}`);
  if (Array.isArray(data)) return data;
  return data?.notes || [];
}

/**
 * Create a note. author is derived server-side from the auth context.
 *
 * When `parentId` is provided the note is a reply: the server inherits
 * scope/project_id/bundle_id from the parent and forces it plain
 * (is_decision=false, note_type='note'). Root notes omit parentId.
 * @param {{scope:'project'|'bundle'|'general', projectId?:string, bundleId?:string,
 *   body:string, noteType?:string, isDecision?:boolean, parentId?:string}} args
 */
export async function createNote({ scope, projectId, bundleId, taskId, dueAt, body, noteType, isDecision, parentId } = {}) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({
      action: 'notes.create',
      scope,
      project_id: projectId || undefined,
      bundle_id: bundleId || undefined,
      task_id: taskId || undefined,
      due_at: dueAt || undefined,
      body,
      note_type: noteType || undefined,
      is_decision: isDecision || undefined,
      parent_id: parentId || undefined,
      source: 'chat',
    }),
  });
}

/** Soft-archive a note (sets archived_at server-side). */
export async function archiveNote(id) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({ action: 'notes.archive', id }),
  });
}

/** Update an existing note's body / type / decision flag. */
export async function updateNote({ id, body, noteType, isDecision } = {}) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({
      action: 'notes.update',
      id,
      body,
      note_type: noteType,
      is_decision: isDecision,
    }),
  });
}

/**
 * TICKET-735 — reminder notes due within the window (default 7 days) or overdue.
 * @returns {Promise<Array>} notes rows (note_type=reminder, due_at ascending)
 */
export async function listRemindersDue({ withinHours } = {}) {
  const params = new URLSearchParams({ action: 'notes.reminders_due' });
  if (withinHours) params.set('within_hours', String(withinHours));
  const data = await apiFetch(`${DATA}?${params}`);
  return Array.isArray(data) ? data : (data?.notes || []);
}
